import {
  IVirtualManifest,
  IVirtualManifestItemMapping,
  ReducibleKey,
} from '../builders';
import { ManifestComponentExtensionType } from '../stores';
import type { CompRef } from '@wix/document-services-types';
import { expandCssPropertiesMappings } from '../builders/expandCSSProperties';
import { VirtualElement } from './providers/VirtualElement';

import type { EditorPointer } from '@wix/editor-document-logic-api';

export class VirtualManifestOperations {
  #virtualManifest: IVirtualManifest;

  constructor(protected readonly node: VirtualElement<any>) {
    this.#virtualManifest = node.getVirtualManifest();
  }

  getMappings(
    editorPointer: EditorPointer<CompRef>,
    key: ReducibleKey,
    options = { shouldExpandCssProperties: false },
  ): Record<string, IVirtualManifestItemMapping[]> {
    const mappings = this.#virtualManifest.tree.mappings[key] ?? {};

    const editorPointerMappings = Object.fromEntries(
      Object.entries(mappings)
        .map(([id, items]) => [
          id,
          items.filter(
            (x) => x.editorPointer.asUniqueKey === editorPointer.asUniqueKey,
          ),
        ])
        .filter(([, filteredItems]) => filteredItems.length > 0), // remove empty arrays
    );

    return options.shouldExpandCssProperties && key === 'cssProperties'
      ? expandCssPropertiesMappings(editorPointerMappings)
      : editorPointerMappings;
  }

  getEditorPointers(...keys: ReducibleKey[]) {
    const items = (
      keys ? keys : Object.keys(this.#virtualManifest.tree.mappings)
    ) as ReducibleKey[];

    const contexts: Record<
      string,
      {
        editorPointer: EditorPointer<CompRef>;
        extensionType: ManifestComponentExtensionType;
      }
    > = {};

    const mappings = this.#virtualManifest.tree.mappings;

    items.forEach((_key) => {
      Object.values(mappings[_key] ?? {})
        .flat()
        .forEach((x) => {
          if (contexts[x.editorPointer.asUniqueKey]) {
            return;
          }

          contexts[x.editorPointer.asUniqueKey] = {
            editorPointer: x.editorPointer,
            extensionType: x.extensionType,
          };
        });
    });

    return Object.values(contexts);
  }

  decodeValues<TValue extends Record<string, unknown>>(
    values: TValue,
    mappings: Record<string, IVirtualManifestItemMapping[]>,
  ) {
    if (!values) {
      return {} as TValue;
    }

    const decodedValues = {} as TValue;

    Object.keys(mappings).forEach((runtimeKey) => {
      mappings[runtimeKey].forEach((config) => {
        if (!(config.originalKey in values)) {
          return;
        }

        const originalValue = values[config.originalKey];

        (decodedValues as Record<string, unknown>)[runtimeKey] =
          config.decodeValue
            ? config.decodeValue(originalValue)
            : originalValue;
      });
    });

    return decodedValues;
  }

  encodeValues<TValue extends Record<string, unknown>>(
    values: TValue,
    mappings: Record<string, IVirtualManifestItemMapping[]>,
  ) {
    if (!values) {
      return {};
    }

    const encodedValues = {} as TValue;

    Object.keys(mappings).forEach((runtimeKey) => {
      mappings[runtimeKey].forEach((config) => {
        if (!(config.runtimeKey in values)) {
          return;
        }

        const originalValue = values[config.runtimeKey];

        (encodedValues as Record<string, unknown>)[config.originalKey] =
          config.encodeValue
            ? config.encodeValue(originalValue, '')
            : originalValue;
      });
    });

    return encodedValues;
  }

  getRuntimeKeys(
    keys: string[],
    mappings: Record<string, IVirtualManifestItemMapping[]>,
  ) {
    const runtimeKeys: string[] = [];

    Object.keys(mappings).forEach((runtimeKey) => {
      if (!keys.includes(runtimeKey)) {
        return;
      }

      mappings[runtimeKey].forEach((config) => {
        runtimeKeys.push(config.runtimeKey);
      });
    });

    return runtimeKeys;
  }

  getOriginalKeys(
    keys: string[],
    mappings: Record<string, IVirtualManifestItemMapping[]>,
  ) {
    const originalKeys: string[] = [];

    Object.keys(mappings).forEach((runtimeKey) => {
      if (!keys.includes(runtimeKey)) {
        return;
      }

      mappings[runtimeKey].forEach((config) => {
        originalKeys.push(config.originalKey);
      });
    });

    return originalKeys;
  }

  mergeValues<TValue>(
    values: Record<string, TValue>[],
  ): Record<string, TValue> {
    if (values.length === 1) {
      return values[0];
    }

    const pickValue = (prev: any, next: any) =>
      prev === undefined ? next : prev === next ? next : 'MIX';

    return values.reduce((acc, obj) => {
      Object.entries(obj).forEach(([key, value]) => {
        acc[key] = pickValue(acc[key], value);
      });

      return acc;
    }, {});
  }
}
